/**
 * FaqSection Component
 * Frequently Asked Questions section for court cases
 */

import { Link } from '@ltvco/refresh-lib/theme';
import React, { useState } from 'react';

import {
  FaqContainer,
  HeaderContainer,
  FaqTitle,
  SubtitleText,
  StyledAccordion,
  QuestionText,
  AnswerText,
  AnswerList,
  DisclaimerBox,
  DisclaimerText,
} from './components';

// ============= TYPES =============
export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  list?: string[];
}

export interface FaqSectionProps {
  title?: string;
  subtitle?: string;
  contactUrl?: string;
  contactText?: string;
  faqItems: FaqItem[];
  disclaimer?: string;
  testId?: string;
}

// ============= COMPONENT =============
export const FaqSection: React.FC<FaqSectionProps> = ({
  title = 'Frequently Asked Questions',
  subtitle = 'Still need help? Feel free to',
  contactUrl = 'https://www.beenverified.com/contact/',
  contactText = 'contact us',
  faqItems,
  disclaimer,
  testId = 'faq-section',
}) => {
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);

  const handleAccordionChange = (itemId: string) => {
    setExpandedPanel((prev) => (prev === itemId ? null : itemId));
  };

  return (
    <FaqContainer data-testid={testId}>
      {/* Header */}
      <HeaderContainer>
        <FaqTitle variant="h6">{title}</FaqTitle>
        <SubtitleText>
          {subtitle}{' '}
          <Link href={contactUrl} target="_blank" rel="noopener noreferrer">
            {contactText}
          </Link>
        </SubtitleText>
      </HeaderContainer>

      {/* FAQ Items */}
      {faqItems.map((item) => (
        <StyledAccordion
          key={item.id}
          display={<QuestionText>{item.question}</QuestionText>}
          expanded={expandedPanel === item.id}
          onChange={() => handleAccordionChange(item.id)}
        >
          <AnswerText>{item.answer}</AnswerText>
          {item.list && item.list.length > 0 && (
            <AnswerList>
              {item.list.map((listItem, index) => (
                <li key={index}>{listItem}</li>
              ))}
            </AnswerList>
          )}
        </StyledAccordion>
      ))}

      {/* Disclaimer */}
      {disclaimer && (
        <DisclaimerBox>
          <DisclaimerText>{disclaimer}</DisclaimerText>
        </DisclaimerBox>
      )}
    </FaqContainer>
  );
};

export default FaqSection;
